import initTypeFilter from './type-filter';

interface PortfolioResponse {
	html: string;
	more: string;
	page: number;
	pages: number;
}

function initListing(root: HTMLElement): void {
	const grid = root.querySelector<HTMLElement>('[data-portfolio-grid]');
	const footer = root.querySelector<HTMLElement>('[data-portfolio-footer]');
	const endpoint = root.dataset.endpoint;

	if (!grid || !footer || !endpoint) {
		return;
	}

	let type = root.dataset.type ?? '';
	let controller: AbortController | null = null;

	// The filter is kept in the address, so a shared link opens on the same type.
	const syncUrl = (): void => {
		const url = new URL(window.location.href);

		url.searchParams.delete('pg');

		if (type) {
			url.searchParams.set('type', type);
		} else {
			url.searchParams.delete('type');
		}

		window.history.replaceState(window.history.state, '', url);
	};

	const load = async (page: number, append: boolean): Promise<void> => {
		controller?.abort();
		controller = new AbortController();

		const params = new URLSearchParams({
			type,
			page: String(page),
			per_page: root.dataset.perPage ?? '12',
			more: root.dataset.more ?? '',
			base: root.dataset.base ?? '',
		});

		root.classList.add('is-loading');
		grid.setAttribute('aria-busy', 'true');

		try {
			const response = await fetch(`${endpoint}?${params.toString()}`, {
				signal: controller.signal,
				headers: { Accept: 'application/json' },
			});

			if (!response.ok) {
				throw new Error(String(response.status));
			}

			const data = (await response.json()) as PortfolioResponse;

			if (append) {
				grid.insertAdjacentHTML('beforeend', data.html);
			} else {
				grid.innerHTML = data.html;
			}

			footer.innerHTML = data.more;
			controller = null;
		} catch (error) {
			if ((error as Error).name === 'AbortError') {
				return;
			}

			controller = null;

			// The links work without the script, so fall back to a page load.
			const fallback = new URL(root.dataset.base ?? window.location.href);

			if (type) {
				fallback.searchParams.set('type', type);
			}

			if (page > 1) {
				fallback.searchParams.set('pg', String(page));
			}

			window.location.href = fallback.toString();
		} finally {
			if (!controller) {
				root.classList.remove('is-loading');
				grid.removeAttribute('aria-busy');
			}
		}
	};

	const choose = (next: string): void => {
		if (next === type && !controller) {
			return;
		}

		type = next;
		filter?.set(type);
		syncUrl();
		void load(1, false);
	};

	const filter = initTypeFilter(root, choose);

	footer.addEventListener('click', (event) => {
		const more = (event.target as HTMLElement).closest<HTMLAnchorElement>('[data-portfolio-more]');

		if (more) {
			event.preventDefault();
			void load(Number(more.dataset.portfolioMore), true);
		}
	});
}

export default function initPortfolio(): void {
	document.querySelectorAll<HTMLElement>('[data-armada-portfolio]').forEach(initListing);
}
